import { useCallback, useEffect, useState } from 'react';
import { useApi, type ToolConfig } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { ConnectedApps } from '@/components/ConnectedApps';
import { CustomToolForm } from '@/components/CustomToolForm';
import { McpServerForm } from '@/components/McpServerForm';
import { BuiltInTools } from '@/components/BuiltInTools';
import { Plug, Wrench, Server, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const TABS = [
  { key: 'apps',    label: 'Connected apps', Icon: Plug },
  { key: 'custom',  label: 'Custom tools',   Icon: Wrench },
  { key: 'mcp',     label: 'MCP servers',    Icon: Server },
  { key: 'builtin', label: 'Built-in',       Icon: Sparkles },
] as const;

type TabKey = typeof TABS[number]['key'];

export function Tools() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <ToolsBody orgId={me.org!.id} />}</MeGate>;
}

function ToolsBody({ orgId }: { orgId: string }) {
  const api = useApi();
  const [tab, setTab] = useState<TabKey>('apps');
  const [tools, setTools] = useState<ToolConfig[]>([]);

  const reload = useCallback(() => {
    void api
      .get<{ tools: ToolConfig[] }>(`/api/orgs/${orgId}/tools`)
      .then((r) => setTools(r.tools));
  }, [api, orgId]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="h-full overflow-y-auto">
      <header className="p-6 border-b bg-background">
        <h1 className="text-xl font-semibold">Tools</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect apps, add custom tools, and register MCP servers the AI agent can call on behalf of your customers.
        </p>
      </header>
      <nav className="px-6 pt-4 border-b bg-background">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 -mb-px transition-colors',
                tab === t.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <t.Icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="p-6">
        {tab === 'apps'    && <ConnectedApps orgId={orgId} tools={tools} onChanged={reload} />}
        {tab === 'custom'  && <CustomToolForm orgId={orgId} tools={tools} onChanged={reload} />}
        {tab === 'mcp'     && <McpServerForm  orgId={orgId} tools={tools} onChanged={reload} />}
        {tab === 'builtin' && <BuiltInTools   orgId={orgId} tools={tools} onChanged={reload} />}
      </div>
    </div>
  );
}
